import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import { buildUrlWithLang } from "@/lib/utils";

const Hero = () => {
  const { language, t } = useLanguage();

  const heroImageSchema = {
    "@context": "https://schema.org",
    "@type": "ImageObject",
    "contentUrl": "https://www.kareemtaxi.co.mz/images/motorista-yango-maputo-1024.avif",
    "url": "https://www.kareemtaxi.co.mz/",
    "name": "Motorista Taxi Yango Maputo - Taxi Online em Maputo",
    "caption": "Cadastro motorista Yango Maputo - Criar conta Yango com Kareem Taxi parceiro oficial - App transporte Maputo",
    "description": "Imagem de motorista Yango em Maputo mostrando oportunidade de cadastro e criação de conta com Kareem Taxi, parceiro oficial Yango em Moçambique. Taxi online em Maputo com app transporte.",
    "creator": {
      "@type": "Organization",
      "name": "Kareem Taxi",
      "url": "https://www.kareemtaxi.co.mz/"
    },
    "author": {
      "@type": "Organization",
      "name": "Kareem Taxi",
      "url": "https://www.kareemtaxi.co.mz/"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kareem Taxi",
      "url": "https://www.kareemtaxi.co.mz/"
    },
    "creditText": "Kareem Taxi - Parceiro Oficial Yango",
    "copyrightNotice": "© 2025 Kareem Taxi",
    "license": "https://www.kareemtaxi.co.mz/termos-de-uso#imagens",
    "acquireLicensePage": "https://wa.me/258853448609?text=Gostaria%20de%20adquirir%20licença%20para%20uso%20de%20imagem",
    "keywords": "cadastro motorista Yango Maputo, criar conta Yango, parceiro oficial Yango, inscrição motorista Yango Moçambique, yango partner mozambique, motorista taxi maputo, taxi online maputo, app transporte maputo"
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(heroImageSchema)}
        </script>
      </Helmet>
      <section
        id="home"
        className="
          relative
          min-h-[calc(100svh-5rem)]
          lg:min-h-screen
          flex flex-col lg:grid lg:grid-cols-2
          overflow-visible
          pt-28 lg:pt-32
          scroll-mt-24
        "
      >
        {/* Left: Driver Recruitment Content */}
        <div className="bg-background flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-16 pb-6 md:py-12 lg:py-20 order-1 lg:order-1">
          <div className="w-full max-w-3xl mx-auto text-center lg:text-left lg:mx-0">
            {/* H1 Headline */}
            <h1
              className="
                text-3xl sm:text-4xl md:text-5xl lg:text-6xl
                font-bold mb-4 lg:mb-5
                leading-[1.2]
                break-words
                lg:pr-8 text-foreground
              "
            >
              {t("hero.title")}
            </h1>

            {/* Short intro - visible on mobile, hidden on desktop (full text shows on desktop) */}
            <p className="text-base sm:text-lg text-muted-foreground mb-6 leading-relaxed lg:hidden">
              {t("hero.shortIntro")}
            </p>

            {/* Full SEO paragraph - hidden on mobile, visible on desktop */}
            <p className="hidden lg:block text-base sm:text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed">
              {t("hero.subtitle")}
            </p>

            {/* Primary CTA - prominent on mobile */}
            <div className="flex flex-col items-center lg:items-start gap-4">
              <Button
                size="lg"
                className="
                  w-full sm:w-auto
                  whitespace-normal break-words
                  text-lg px-8 py-5
                  bg-primary text-primary-foreground hover:bg-primary/90
                  font-bold tracking-wide
                "
                onClick={() => {
                  window.open(
                    "https://forms.fleet.yango.com/forms?specification=taxi&ref_id=b6140518c1b9412f9df3576a9f32d6d1&utm_source=google&utm_medium=cpc&utm_campaign=motors_car_search&lang=pt",
                    "_blank",
                  );
                }}
              >
                {t("hero.driverCard.cta")}
              </Button>
              
              {/* Trust micro-line - visible below CTA */}
              <p className="text-sm text-muted-foreground">
                {t("hero.trustLine")}
              </p>
            </div>
            
            {/* Secondary links */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start text-sm mt-6">
              <a href={buildUrlWithLang('/cadastro-motorista-yango', language)} className="text-muted-foreground hover:text-primary underline transition-colors">
                {language === 'pt' ? 'Ver guia completo de cadastro →' : 'See complete registration guide →'}
              </a>
              <a href={buildUrlWithLang('/requisitos-motorista-yango', language)} className="text-muted-foreground hover:text-primary underline transition-colors">
                {language === 'pt' ? 'Requisitos necessários →' : 'Required requirements →'}
              </a>
            </div>
          </div>
        </div>

        {/* Hero Image - order 2 on mobile (after content), order 2 on desktop */}
        <div className="relative w-full order-2 lg:order-2 aspect-[2/3] lg:aspect-auto lg:h-screen">
          <picture className="block w-full h-full">
            <source
              type="image/avif"
              srcSet="/images/motorista-yango-maputo-320.avif 320w,
                      /images/motorista-yango-maputo-480.avif 480w,
                      /images/motorista-yango-maputo-640.avif 640w,
                      /images/motorista-yango-maputo-720.avif 720w,
                      /images/motorista-yango-maputo-1024.avif 1024w"
              sizes="(max-width: 374px) 320px,
                     (max-width: 479px) 480px,
                     (max-width: 639px) 640px,
                     (max-width: 767px) 720px,
                     720px"
            />
            <source
              type="image/webp"
              srcSet="/images/motorista-yango-maputo-320.webp 320w,
                      /images/motorista-yango-maputo-480.webp 480w,
                      /images/motorista-yango-maputo-640.webp 640w,
                      /images/motorista-yango-maputo-720.webp 720w,
                      /images/motorista-yango-maputo.webp 1024w"
              sizes="(max-width: 374px) 320px,
                     (max-width: 479px) 480px,
                     (max-width: 639px) 640px,
                     (max-width: 767px) 720px,
                     720px"
            />
            <img
              src="/images/motorista-yango-maputo.webp"
              alt="Motorista taxi Yango em Maputo - App taxi online Maputo - Motorista aplicativo Maputo"
              title="Cadastro Motorista Yango Maputo | Criar Conta Yango | Taxi Online Maputo | Kareem Taxi - Parceiro Oficial"
              className="w-full h-full object-cover object-right"
              width="1024"
              height="1536"
              loading="eager"
              fetchPriority="high"
              decoding="sync"
            />
          </picture>
        </div>

      </section>
    </>
  );
};

export default Hero;